import vuei18n from '@/plugins/vue-i18n';
import { validateDate } from '@/utils'
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const LOWER_CASE_REGEX = /[a-z]/;
const UPPER_CASE_REGEX = /[A-Z]/;
const NUMBER_REGEX = /[0-9]/;

const inputRules = {
  required: value => !!value || vuei18n.t('errors.inputValidations.required'),
  passwordConfirmation: (x, y) => x === y || vuei18n.t('changePassword.badConfirmPassword'),
  email: value => EMAIL_REGEX.test(value) || vuei18n.t('errors.inputValidations.invalidFormat'),
  maxLength: maxLength => value => {
      if (!value) {
          return true;
      }
      return value.length <= maxLength || vuei18n.tc('errors.inputValidations.maxLength', [maxLength])
  },
  min:  minLength=> value =>
    (value && value.length >= minLength) || vuei18n.tc('updatePassword.passwordMinLength', [minLength]),
  specialCharacter: characters => value => (value && hasSpecialChars(value, characters)) || vuei18n.tc('updatePassword.specialCharacter', [characters]),
  lowerCase: value => 
    (value && LOWER_CASE_REGEX.test(value)) || vuei18n.t('updatePassword.passwordLowerCase'),
  upperCase: value =>
    (value && UPPER_CASE_REGEX.test(value)) || vuei18n.t('updatePassword.passwordUpperCase'),
  number: value =>
    (value && NUMBER_REGEX.test(value)) || vuei18n.t('updatePassword.number'),
  fileLimit: (value, maxFiles) => {
    if (!value) {
      return true;
    }
    return value.length <= maxFiles || vuei18n.tc('errors.inputValidations.maxFiles', [maxFiles])
  },
  zero: value => {
    if (!value) {
      return true;
    }
    return value > 0 || vuei18n.t('errors.inputValidations.invalid')
  },
  date: value => {
    if (!value) {
      return true;
    }

    return validateDate({ value }) || vuei18n.t('errors.inputValidations.invalidDate')
  },    
  maxValue: (value, maxValue) => {
    if (!value) {
        return true;
    }
    return value && value <= maxValue || vuei18n.tc('errors.inputValidations.invalid', [maxValue])
  },
  diagnosisRepeated: (value, diagnosisType) => {
    const message = diagnosisType === 'MEDICAL_DIAGNOSIS' ? 'diagnoses.errors.repeated' : 'patients.allergies.errors.repeated';

    return !value || vuei18n.t(message)
  },
  dateOfBirth: value => validateDate({ value }) || vuei18n.t('errors.inputValidations.invalidBirthday'),
}


function hasSpecialChars(str, characters) {
  const specialCharsSet = new Set(characters);
  for (let letter of str) {
    if (specialCharsSet.has(letter)) {
      return true;
    }
  }
  return false;
}

function inputRulesFromSponsorForm(fieldProperties){
  let rules = []
  if(fieldProperties.required){
      let requiredFunction = (v) => !!v || vuei18n.t('errors.inputValidations.required')
      rules.push(requiredFunction);
  }

  if(fieldProperties.pattern){
      let pattern = new RegExp(fieldProperties.pattern);
      let patternFunction = (v) => v && pattern.test(v) || vuei18n.t('errors.inputValidations.invalidFormat')
      rules.push(patternFunction)
  }

  if(fieldProperties.maxLength){
      let maxLengthFunction = (v) => !!v && v.length <= fieldProperties.maxLength || vuei18n.tc('errors.inputValidations.maxLength', [fieldProperties.maxLength])
      rules.push(maxLengthFunction)
  }

  if(fieldProperties.minLength){
      let minLengthFunction = (v) => !!v && v.length >= fieldProperties.minLength || vuei18n.tc('errors.inputValidations.minLength', [fieldProperties.minLength])
      rules.push(minLengthFunction)
  }

  return rules;
}

function getFieldsByForm(authorizationForms) {
  const firstStep = authorizationForms
    .map(form => ({ key: form.formSlug, label: form.fallbackName, coverageType: form.authorizationCoverage}));
  
  const forms = authorizationForms
  .map(form => ({ form: form.formSlug }))

  const fieldsByForm = forms.reduce((acc, current) => {
    return {
      ...acc,
      [current.form]: [
        [
          { 
            required: true, 
            label: vuei18n.t('addAuthorization.fields.type'), 
            type: 'SELECT', 
            options: firstStep, 
            slug: 'forms', 
            description: vuei18n.t('addAuthorization.fields.description'),
            groupName: vuei18n.t('addAuthorization.fields.type')
          }
        ]
      ]
    }
  }, {});
  
  authorizationForms.forEach(form => {
    const fieldsByGroup = getFormFields(form).reduce((actual, current) => {
      actual[current.groupName] = actual[current.groupName] || [];
      actual[current.groupName].push(current);
      return actual;
    }, {})
    let mins = []
    Object.keys(fieldsByGroup)
    .forEach(group => {
      const min = fieldsByGroup[group].reduce((actual, current) => {
        return actual.showOrder < current.showOrder ? actual : current; 
      })
      mins.push(min);
    })  

    const order = mins.sort((a,b) => {
      if (a.showOrder > b.showOrder) return 1;
      if (b.showOrder > a.showOrder) return -1;
      return 0;
    });

    order.forEach(step => {
      let formGroup;
      Object.keys(fieldsByGroup)
      .forEach(group => {
        const act = fieldsByGroup[group].find(value => value === step);
        if (act) {
          formGroup = fieldsByGroup[group].sort((a,b) => {
            if (a.showOrder > b.showOrder) return 1;
            if (b.showOrder > a.showOrder) return -1;
            return 0;
          });
        }
      })
      fieldsByForm[form.formSlug].push(formGroup);
      formGroup = null;
    })
  })
  return fieldsByForm;
}

function groupBy(array, key) {
  return array.reduce(function(rv, x) {
    (rv[x[key]] = rv[x[key]] || []).push(x);
    return rv;
  }, {});
}

function getFormFields(form) {
  const { authorizationCoverage, fields } = form
  if (authorizationCoverage === 'CREDIT_CARD_BALANCE') {
    return fields.filter(field => field.slug !== 'settlement_files')
  } else if (authorizationCoverage === 'MEDICAL_FEES') {
    return fields.filter(field => field.slug !== 'pdf_invoice' && field.slug !== 'xml_invoice')
  }
  return fields
}

export { inputRules, inputRulesFromSponsorForm, getFieldsByForm, groupBy }
